import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import { FormMode } from 'common/constants/enums';
import type { IPageFormV2 } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { FC, useEffect, useRef } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import slugify from 'slugify';
import { pagesApiSlice } from 'store/api/pages-api/pages-api';

export const KeywordInput: FC<{ formMode: FormMode }> = ({ formMode }) => {
  const { control, setValue } = useFormContext<IPageFormV2>();

  const brandId = useWatch({ control, name: 'brand.id' });
  const publisherId = useWatch({ control, name: 'publisher.id' });
  const domainId = useWatch({ control, name: 'domain_id' });

  const {
    field,
    fieldState: { error },
  } = useController({ name: 'title', control });

  const [validateAggPageSlug, { data, error: mutationError, isLoading: isChecking, reset }] =
    pagesApiSlice.useValidateAggPageSlugV2Mutation();

  const isFirstRender = useRef(true);
  const hasReceivedInitialValue = useRef(false);
  const initialSlugRef = useRef<string>('');

  const isCreateMode = formMode !== FormMode.UPDATE && formMode !== FormMode.SEARCH;

  useEffect(() => {
    let delayDebounceFn: NodeJS.Timeout;

    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else if (!isCreateMode && !hasReceivedInitialValue.current) {
      // Capture the original slug when the form data first loads in UPDATE mode
      hasReceivedInitialValue.current = true;
      initialSlugRef.current = field.value
        ? slugify(field.value, { lower: true, strict: true })
        : '';
    } else if (!field.value) {
      reset();
      setValue('slug', '');
    } else {
      const generatedSlug = slugify(field.value, { lower: true, strict: true });
      setValue('slug', generatedSlug);

      // Skip the request if the slug hasn't changed from the original
      if (!isCreateMode && generatedSlug === initialSlugRef.current) {
        reset();
      } else {
        delayDebounceFn = setTimeout(() => {
          validateAggPageSlug({
            slug: generatedSlug,
            brand_id: brandId || undefined,
            publisher_id: publisherId || undefined,
            domain_id: domainId || undefined,
          });
        }, 500);
      }
    }

    return () => {
      if (delayDebounceFn) clearTimeout(delayDebounceFn);
    };
  }, [field.value, brandId, publisherId, domainId]);

  useEffect(() => {
    return () => reset();
  }, []);

  const isReduxError = data?.exists === true || !!mutationError;
  const isReduxSuccess = data?.exists === false;
  const hasError = !!error || isReduxError;

  const generatedSlug = field.value
    ? slugify(field.value, { lower: true, strict: true })
    : '';

  let helperText = error?.message;
  if (!helperText && data?.exists) {
    helperText = 'Slug already exists';
  } else if (!helperText && isReduxSuccess) {
    helperText = 'Slug is available';
  } else if (mutationError) {
    helperText = 'Error checking slug availability';
  }

  return (
    <Box>
      <TextField
        {...field}
        autoComplete="off"
        color={isReduxSuccess && !hasError ? 'success' : 'primary'}
        error={hasError}
        focused={isReduxSuccess ? true : undefined}
        fullWidth
        helperText={
          helperText && (
            <Typography
              color={isReduxSuccess && !hasError ? 'success.main' : 'inherit'}
              component="span"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              variant="caption"
            >
              {isReduxSuccess && !hasError && <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
              {isReduxError && <ErrorOutlineIcon sx={{ fontSize: 14 }} />}
              {helperText}
            </Typography>
          )
        }
        label="Title"
        size="small"
        slotProps={{
          input: {
            endAdornment: isChecking ? (
              <InputAdornment position="end">
                <CircularProgress color="inherit" size={20} />
              </InputAdornment>
            ) : null,
            sx: { fontSize: '1.1rem', fontWeight: 500 },
          },
        }}
      />

      {isCreateMode && generatedSlug && (
        <Typography color="text.secondary" sx={{ mt: 0.75, ml: 0.25 }} variant="caption">
          Slug:{' '}
          <Typography color="text.primary" component="span" fontWeight={500} variant="caption">
            {generatedSlug}
          </Typography>
        </Typography>
      )}
    </Box>
  );
};
